/**
 * Pure mapping helpers for the post-combat log path (#259 / 07 §13.9.2).
 *
 * No side effects — the output describes WHAT ops to emit; the reducer
 * translates the bundle descriptor into Effects. Never touches the op log
 * directly; never constructs VisibleFact/VisibleOp.
 */

import { mintBundleId, mintEntityId } from '@dl/shared';

import type { InitiativeEntry, InitiativeState } from './initiative.js';

/** Participant fate — derived from hp_scratch or defaulting to survived. */
export type ParticipantFate = 'survived' | 'killed';

/**
 * Derive outcome fate from an initiative entry's hp_scratch.
 * Kills if hp_scratch.current is set and <= 0; otherwise survived.
 */
export function participantsFate(entry: InitiativeEntry): ParticipantFate {
  if (entry.hp_scratch !== undefined && entry.hp_scratch.current <= 0) return 'killed';
  return 'survived';
}

/**
 * The minimal descriptor for a combat-log bundle — ids + snapshot.
 * Reducer uses this to emit the actual op-log Effects.
 */
export interface CombatLogBundle {
  /** `bun_<uuid>` — the bundle id for the declare + member back-links. */
  bundleId: string;
  /** `event_<uuid>` — the new Event entity for this encounter. */
  eventEntityId: string;
  /** Snapshot of the initiative state at end-encounter time. */
  snapshot: InitiativeState;
  /** Optional location entity ref (auto-filled from scene/last-pinned). */
  locationRef?: string;
  /** Display title for the Event name fact. */
  title: string;
}

/** Build a new `CombatLogBundle` descriptor from the current initiative state. */
export function buildCombatLogBundle(
  state: InitiativeState,
  locationRef?: string,
): CombatLogBundle {
  const bundleId = mintBundleId();
  const eventEntityId = mintEntityId('event');
  const round = state.round;
  const title = `Encounter — Round ${round}`;
  return {
    bundleId,
    eventEntityId,
    snapshot: state,
    ...(locationRef !== undefined ? { locationRef } : {}),
    title,
  };
}

/**
 * Structured value for the `participants_template` fact on the Event.
 * Used for fodder entries (no `character_ref`) — no per-goblin entities.
 */
export interface ParticipantsTemplateValue {
  template: null;
  count: number;
  fates: readonly { display_name: string; fate: ParticipantFate }[];
  derived_from: 'initiative_state_snapshot';
}

/**
 * Build the `participants_template` structured value from fodder initiative
 * entries (those without a `character_ref`). Returns null when there are
 * no fodder entries (caller should skip the fact).
 */
export function buildParticipantsTemplateValue(
  entries: InitiativeEntry[],
  fateOverrides: Record<string, ParticipantFate> = {},
): ParticipantsTemplateValue | null {
  const fodder = entries.filter((e) => !e.character_ref);
  if (fodder.length === 0) return null;
  return {
    template: null,
    count: fodder.length,
    fates: fodder.map((e) => ({
      display_name: e.display_name,
      fate: fateOverrides[e.id] ?? participantsFate(e),
    })),
    derived_from: 'initiative_state_snapshot',
  };
}
